import type { Tracker } from '@archipro-website/tracker';
import type { FilterStates } from '~/modules-rocco/filters/types/filter-states';
import { hasActiveFilterStates } from '~/modules-rocco/filters/data-mappers/filter-states-to-url';

interface SearchResultsExtraData {
    query: string;
    totalResults: number;
    category?: string;
    hasFilters: boolean;
}

/**
 * Emit the `SearchResults` event with the shared dashboard payload format.
 *
 * @param tracker The tracker instance used to send the event.
 * @param payload The search metadata that will be serialised into `ExtraData`.
 */
export function trackSearchResults({
    tracker,
    ...payload
}: {
    tracker: Tracker;
    query: string;
    totalResults: number;
    filterStates?: FilterStates | null;
}) {
    tracker.log('SearchResults', {
        url: new URL(window.location.href),
        data: {
            ExtraData: buildSearchResultsExtraData(payload),
        },
    });
}

/**
 * Check whether any non-category filters are currently applied.
 *
 * @param filterStates The current Rocco filter state object.
 * @returns `true` when filters other than category or sort are active.
 */
export function hasAppliedSearchResultFilters(
    filterStates?: FilterStates | null,
) {
    if (!filterStates) {
        return false;
    }

    return hasActiveFilterStates(filterStates, ['category'], false, false);
}

/**
 * Build the canonical `ExtraData` payload for the `SearchResults` event.
 *
 * @param query The raw search query entered by the user.
 * @param totalResults The total number of matched results.
 * @param filterStates The current Rocco filter state object.
 * @returns The normalised payload stored under `data.ExtraData`.
 */
export function buildSearchResultsExtraData({
    query,
    totalResults,
    filterStates,
}: {
    query: string;
    totalResults: number;
    filterStates?: FilterStates | null;
}): SearchResultsExtraData {
    const trimmedQuery = query.trim();
    const category = filterStates?.states.category?.selectedCategory?.url;

    return {
        query: trimmedQuery,
        totalResults,
        ...(category ? { category } : {}),
        hasFilters: hasAppliedSearchResultFilters(filterStates),
    };
}
